import React, { useState, useEffect, useCallback } from 'react'
import styles from './ColorsManagement.module.css'
import cx from 'classnames'
import Heading from '../../components/Heading'
import Input from '../../components/Input'
import { ColorService } from '../../services/ColorService'
import { Color } from '../../types/Color'
import { showToast } from '../../services/ToastService'
import { openModal, closeModal } from '../../services/ModalService'
import SketchColorPicker from '../../components/SketchColorPicker'
import ColorListView from './ColorListView'
import Icon from '../../components/Icon'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { DeleteColorModal } from './DeleteColorModal'

const ColorsManagement: React.FC = () => {
    const [colors, setColors] = useState<Color[]>([])
    const [loading, setLoading] = useState<boolean>(true)
    const [name, setName] = useState<string>('')
    const [color, setColor] = useState<string>('#ff0000')
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    useEffect(() => {
        loadColors()
    }, [])

    const loadColors = async () => {
        try {
            setLoading(true)
            const fetchedColors = await ColorService.getAllColors()
            setColors(fetchedColors)
        } catch (error) {
            showToast('Fehler beim Laden der Farben', 'error')
        } finally {
            setLoading(false)
        }
    }

    const handleColorChange = (property: string, value: string) => {
        setColor(value)
    }

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault()
        }
        
        if (isSubmitting) {
            return
        }
        
        if (!name.trim()) {
            showToast('Bitte geben Sie einen Namen ein', 'error')
            return
        }

        if (!color.trim() || !/^#[0-9A-Fa-f]{6}$/.test(color)) {
            showToast('Bitte wählen Sie eine gültige Farbe', 'error')
            return
        }

        try {
            setIsSubmitting(true)
            await ColorService.createColor(name, color)
            showToast('Farbe erfolgreich hinzugefügt', 'success')
            setName('')
            setColor('#ff0000')
            await loadColors()
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler'
            showToast(`Fehler beim Hinzufügen: ${errorMessage}`, 'error')
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDeleteColor = useCallback(async (color: Color) => {
        try {
            await ColorService.deleteColor(color.id)
            showToast('Farbe erfolgreich gelöscht', 'success')
            await loadColors()
        } catch (error) {
            showToast('Fehler beim Löschen der Farbe', 'error')
        }
    }, [])

    const openDeleteColorModal = useCallback((color: Color) => {
        const modalIdRef = { current: '' };
        modalIdRef.current = openModal({
            content: (
                <DeleteColorModal 
                    colorId={color.id}
                    colorName={color.name}
                    onConfirm={() => {
                        handleDeleteColor(color)
                        closeModal(modalIdRef.current)
                    }} 
                    onCancel={() => closeModal(modalIdRef.current)}
                />
            ),
            size: 'sm',
            onClose: () => closeModal(modalIdRef.current)
        });
    }, [handleDeleteColor])

    return (
        <div className={cx(styles['overview-container'])}>
            <div className={cx(styles['overview-header'])}>
                <Heading level={1} classes={cx(styles['overview-title'])}>
                    Einstellungen
                </Heading>
            </div>

            <div className={cx(styles['colors-management-container'])}>
                <Heading level={3} classes={styles['section-title']}>
                    Farben
                </Heading>

            <form onSubmit={handleSubmit} className={cx(styles['color-form'])}>
                <div className={cx(styles['form-row'])}>
                    <div className={cx(styles['form-group'])}>
                        <Input
                            type="text"
                            placeholder="Name (z.B. rot, grün)"
                            property="name"
                            value={name}
                            onChange={(_, value) => setName(value)}
                            classes={styles['name-input']}
                        />
                    </div>
                    <div className={cx(styles['form-group'])}>
                        <SketchColorPicker
                            property="color"
                            value={color}
                            onChange={handleColorChange}
                        />
                    </div>
                    <div className={cx(styles['form-group'])}>
                        <button
                            type="submit"
                            className={cx(styles['add-color-button'], isSubmitting && styles['add-color-button-disabled'])}
                            disabled={isSubmitting}
                            onClick={(e) => {
                                e.preventDefault()
                                handleSubmit()
                            }}
                        >
                            <Icon icon={faPlus} color="#fff" />
                            <span className={styles['add-button-text']}>
                                {isSubmitting ? 'Wird gespeichert...' : 'Hinzufügen'}
                            </span>
                        </button>
                    </div>
                </div>
            </form>

            {loading ? (
                <div className={cx(styles['loading-container'])}>Farben werden geladen...</div>
            ) : colors.length === 0 ? (
                <div className={cx(styles['empty-state'])}>
                    Noch keine Farben hinzugefügt
                </div>
            ) : (
                <ColorListView
                    colors={colors}
                    onDelete={(id) => {
                        const color = colors.find(c => c.id === id)
                        if (color) openDeleteColorModal(color)
                    }}
                />
            )}
            </div>
        </div>
    )
}

export default ColorsManagement
